import { useState, useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '../../../shared/utils/numberFormat';
import { MASK } from '../../../shared/utils/mask';

interface PureRevenueCalculatorProps {
  revenue: number;
  /** Energy delivered in the period (the stats response's `totalKwh`). */
  kwh: number;
  /** Hide mode: masks the computed figures. Fee inputs stay editable — they
   *  are the operator's own assumptions, not data about the business. */
  hidden?: boolean;
}

interface Fees {
  paymentFee: number;
  tax: number;
  /**
   * UAH per kWh, charged on every kWh delivered in the period. Replaces the
   * flat partner fee: this cost scales with energy, not with revenue.
   */
  pricePerKwh: number;
  services: number;
}

const DEFAULTS: Fees = {
  paymentFee: 1.3,
  tax: 6,
  // No sensible default rate; the operator enters their own.
  pricePerKwh: 0,
  services: 1500,
};

type FieldKey = keyof Fees;

// String representation of DEFAULTS — used to seed the draft state and on reset.
const DEFAULT_DRAFTS: Record<FieldKey, string> = {
  paymentFee: String(DEFAULTS.paymentFee),
  tax: String(DEFAULTS.tax),
  pricePerKwh: String(DEFAULTS.pricePerKwh),
  services: String(DEFAULTS.services),
};

interface FieldConfig {
  key: FieldKey;
  labelKey: string;
  tooltipKey: string;
  icon: string;
  unitKey: string;
  step: number;
}

const FIELDS: FieldConfig[] = [
  { key: 'paymentFee',        labelKey: 'dashboard.metrics.paymentFee',  tooltipKey: 'dashboard.metrics.tooltipPaymentFee', icon: 'credit_card',     unitKey: '%',                          step: 0.1  },
  { key: 'tax',               labelKey: 'dashboard.metrics.tax',         tooltipKey: 'dashboard.metrics.tooltipTax',        icon: 'receipt_long',    unitKey: '%',                          step: 0.1  },
  { key: 'pricePerKwh',       labelKey: 'dashboard.metrics.pricePerKwh', tooltipKey: 'dashboard.metrics.tooltipPricePerKwh', icon: 'bolt',          unitKey: 'dashboard.metrics.uahPerKwh', step: 0.01 },
  { key: 'services',          labelKey: 'dashboard.metrics.services',    tooltipKey: 'dashboard.metrics.tooltipServices',   icon: 'build',           unitKey: 'dashboard.metrics.uah',      step: 1    },
];

export function PureRevenueCalculator({ revenue, kwh, hidden = false }: PureRevenueCalculatorProps) {
  const { t } = useTranslation();
  const uid = useId();
  const [fees, setFees] = useState<Fees>(DEFAULTS);
  // Separate string drafts so the user can clear or partially type decimals
  // without the field snapping back to 0.
  const [drafts, setDrafts] = useState<Record<FieldKey, string>>(DEFAULT_DRAFTS);

  const handleChange = useCallback(
    (key: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setDrafts((prev) => ({ ...prev, [key]: raw }));
      const val = parseFloat(raw);
      if (!isNaN(val) && val >= 0) {
        setFees((prev) => ({ ...prev, [key]: val }));
      }
    },
    [],
  );

  // On blur, commit any pending draft to a valid number (clamp to 0 if empty/NaN).
  const handleBlur = useCallback(
    (key: FieldKey) => () => {
      const val = parseFloat(drafts[key]);
      const safe = isNaN(val) || val < 0 ? 0 : val;
      setFees((prev) => ({ ...prev, [key]: safe }));
      setDrafts((prev) => ({ ...prev, [key]: String(safe) }));
    },
    [drafts],
  );

  const handleReset = useCallback(() => {
    setFees(DEFAULTS);
    setDrafts(DEFAULT_DRAFTS);
  }, []);

  const { costs, totalCosts, pureRevenue } = useMemo(() => {
    const c = {
      paymentFee: revenue * (fees.paymentFee / 100),
      tax: revenue * (fees.tax / 100),
      pricePerKwh: kwh * fees.pricePerKwh,
      services: fees.services,
    };
    const total = c.paymentFee + c.tax + c.pricePerKwh + c.services;
    return { costs: c, totalCosts: total, pureRevenue: revenue - total };
  }, [revenue, kwh, fees]);

  const isPositive = pureRevenue >= 0;
  const percentOfTotal = revenue > 0 ? Math.max(0, Math.min(100, (pureRevenue / revenue) * 100)) : 0;
  const costPct = revenue > 0 ? Math.max(0, Math.min(100, (totalCosts / revenue) * 100)) : 0;
  const netPct = Math.max(0, 100 - costPct);

  const isDirty =
    fees.paymentFee !== DEFAULTS.paymentFee ||
    fees.tax !== DEFAULTS.tax ||
    fees.pricePerKwh !== DEFAULTS.pricePerKwh ||
    fees.services !== DEFAULTS.services;

  const renderUnit = (unitKey: string) => (unitKey === '%' ? '%' : t(unitKey));
  /** Every derived figure goes through here, so hide mode cannot miss one. */
  const show = (value: number) => (hidden ? MASK : formatNumber(value));
  // The split bar states the ratio by its own widths, so masking the numbers
  // beside it would still leave the margin readable off the screen. While
  // hidden it renders as one flat neutral track.
  const barNetPct = hidden ? 0 : netPct;
  const barCostPct = hidden ? 100 : costPct;

  return (
    <div className="pure-revenue-card">
      <div className="pure-revenue-header">
        <div className="pure-revenue-header-main">
          <span className="pure-revenue-icon-chip" aria-hidden="true">
            <span className="material-symbols-outlined">calculate</span>
          </span>
          <div className="pure-revenue-titles">
            <h3 className="pure-revenue-title">{t('dashboard.metrics.pureRevenue')}</h3>
            <p className="pure-revenue-subtitle">{t('dashboard.metrics.pureRevenueSubtitle')}</p>
          </div>
        </div>
        {isDirty && (
          <button type="button" className="pure-revenue-reset" onClick={handleReset}>
            <span className="material-symbols-outlined" aria-hidden="true">restart_alt</span>
            <span>{t('dashboard.metrics.resetDefaults')}</span>
          </button>
        )}
      </div>

      <div className="pure-revenue-body">
        <div className="pure-revenue-inputs">
          {FIELDS.map((field) => {
            const inputId = `${uid}-${field.key}`;
            const amount = costs[field.key];
            return (
              <div key={field.key} className="pure-revenue-field">
                <div className="pure-revenue-field-head">
                  <span className="pure-revenue-field-icon material-symbols-outlined" aria-hidden="true">
                    {field.icon}
                  </span>
                  <label className="pure-revenue-label" htmlFor={inputId}>
                    {t(field.labelKey)}
                  </label>
                  <span
                    className="pure-revenue-info material-symbols-outlined"
                    aria-hidden="true"
                    title={t(field.tooltipKey)}
                  >
                    info
                  </span>
                </div>
                <div className="pure-revenue-input-wrap">
                  <input
                    id={inputId}
                    type="text"
                    inputMode="decimal"
                    className="pure-revenue-input"
                    value={drafts[field.key]}
                    onChange={handleChange(field.key)}
                    onBlur={handleBlur(field.key)}
                  />
                  <span className="pure-revenue-unit">{renderUnit(field.unitKey)}</span>
                </div>
                <div className="pure-revenue-cost">
                  <span className="pure-revenue-cost-arrow material-symbols-outlined" aria-hidden="true">
                    south_east
                  </span>
                  <span>
                    {show(amount)} {t('dashboard.metrics.uah')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={`pure-revenue-result ${isPositive ? 'is-positive' : 'is-negative'}`}>
          <div className="pure-revenue-breakdown">
            <div className="pure-revenue-row">
              <span className="pure-revenue-row-label">{t('dashboard.metrics.totalRevenue')}</span>
              <span className="pure-revenue-row-value">
                {show(revenue)} {t('dashboard.metrics.uah')}
              </span>
            </div>
            <div className="pure-revenue-row pure-revenue-row--cost">
              <span className="pure-revenue-row-label">{t('dashboard.metrics.totalCosts')}</span>
              <span className="pure-revenue-row-value">
                {show(totalCosts)} {t('dashboard.metrics.uah')}
              </span>
            </div>

            <div
              className="pure-revenue-bar"
              role="img"
              aria-label={
                hidden
                  ? t('dashboard.privacy.hiddenValue')
                  : `${t('dashboard.metrics.netShare')} ${Math.round(netPct)}%, ${t('dashboard.metrics.costShare')} ${Math.round(costPct)}%`
              }
            >
              {/* Net leads: what the operator keeps is the headline, and the
                  costs are the remainder rather than the other way round. */}
              <span
                className="pure-revenue-bar-net"
                style={{ width: `${barNetPct}%` }}
              />
              <span
                className="pure-revenue-bar-cost"
                style={{ width: `${barCostPct}%` }}
              />
            </div>
            <div className="pure-revenue-bar-legend">
              <span className="pure-revenue-bar-legend-item">
                <span className="pure-revenue-bar-dot is-net" aria-hidden="true" />
                {t('dashboard.metrics.netShare')} · {hidden ? '••' : Math.round(netPct)}%
              </span>
              <span className="pure-revenue-bar-legend-item">
                <span className="pure-revenue-bar-dot is-cost" aria-hidden="true" />
                {t('dashboard.metrics.costShare')} · {hidden ? '••' : Math.round(costPct)}%
              </span>
            </div>

            <div className={`pure-revenue-row pure-revenue-row--result ${isPositive ? '' : 'pure-revenue-row--negative'}`}>
              <div className="pure-revenue-result-label-wrap">
                <span className="pure-revenue-row-label">{t('dashboard.metrics.pureRevenue')}</span>
                {revenue > 0 && (
                  <span className="pure-revenue-percent-badge">
                    {hidden
                      ? t('dashboard.metrics.percentOfTotal', { percent: '••' })
                      : t('dashboard.metrics.percentOfTotal', { percent: percentOfTotal.toFixed(1) })}
                  </span>
                )}
              </div>
              <span className="pure-revenue-row-value pure-revenue-result-value">
                {show(pureRevenue)} {t('dashboard.metrics.uah')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PureRevenueCalculator;
